<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.gift_type') }}
              </span>
            </p>
            <b-select v-model="filterType" @input="onFilterType">
              <option value="all">{{ $t('attributes.all') }}</option>
              <option value="true">
                {{ $t('attributes.gift_consumable') }}
              </option>
              <option value="false">{{ $t('attributes.gift') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.shelve_status') }}
              </span>
            </p>
            <b-select v-model="status" @input="onFilter">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">
                {{ $t('attributes.shelve_on') }}
              </option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-narrow">
        <div class="buttons">
          <b-button
            v-if="!readonly"
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
            :disabled="!hasPermission(PERMISSION_TYPE.GiftsProcess)"
          >
            {{ $t('attributes.add') + $t('attributes.gift') }}
          </b-button>

          <b-button
            v-if="!readonly"
            :loading="sending"
            class="is-warning"
            :disabled="
              !hasPermission(PERMISSION_TYPE.GiftsPostsProcess) ||
                selectedGifts.length === 0
            "
            @click="onPosting"
          >
            {{ $t('attributes.gift_posting') }}
          </b-button>
          <b-dropdown v-if="canExport()" :triggers="['hover']" aria-role="list">
            <template #trigger>
              <b-button
                :loading="sending"
                type="is-secondary"
                position="is-bottom-left"
                icon-right="menu-down"
              >
                {{ $t('message.export_reports') }}
              </b-button>
            </template>

            <b-dropdown-item
              v-for="option in exportOptions"
              :key="option.value"
              aria-role="listitem"
              :disabled="selectedGifts.length === 0"
              @click="onExport(option.value)"
            >
              <div class="media">
                <div class="media-content">
                  <h3>{{ option.name }}</h3>
                </div>
              </div>
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            :loading="sending"
            class="is-warning"
            :disabled="!hasPermission(PERMISSION_TYPE.GiftsPostsProcess)"
            @click="onCheckLowQuantity"
          >
            檢查所有上架贈品數量是否過少
          </b-button>
        </div>
      </div>
    </nav>

    <section>
      <b-table
        ref="giftListTable"
        :data="items"
        :checked-rows.sync="selectedItems"
        :loading="loadingItems"
        :mobile-cards="false"
        :row-class="rowClass"
        checkable
        hoverable
        narrowed
        :height="giftListTableHeight"
        backend-sorting
        sticky-header
        :default-sort-direction="defaultSortOrder"
        :default-sort="[sortField, sortOrder]"
        @sort="onSort"
        @check="onSelected"
        @check-all="onSelected"
      >
        <b-table-column
          field="id"
          :visible="false"
          :label="$t('attributes.id')"
          width="75"
          sortable
          centered
          v-slot="props"
        >
          {{ props.row.id }}
        </b-table-column>

        <b-table-column
          field="sort"
          :label="$t('attributes.sort')"
          width="50"
          sortable
          centered
          v-slot="props"
        >
          {{ props.row.sort }}
        </b-table-column>

        <b-table-column
          field="picture"
          :label="$t('attributes.picture')"
          width="80"
          height="100"
          centered
          v-slot="props"
        >
          <figure v-if="props.row.picture" class="image">
            <a
              :href="GiftImage(props.row.picture)"
              title="贈品圖片"
              @click.prevent="onClickImage(props.row)"
            >
              <img
                :src="GiftImage(props.row.picture)"
                :alt="$t('attributes.picture')"
                style="width: 80px; height: 80px; margin: auto;"
              />
            </a>
          </figure>
          <p v-else class="has-text-danger has-text-weight-medium">無</p>
        </b-table-column>

        <b-table-column
          field="name"
          :label="$t('attributes.name')"
          width="120"
          sortable
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.name }}
        </b-table-column>

        <b-table-column
          field="shelve_status"
          :label="$t('attributes.shelve_status')"
          width="120"
          sortable
          header-class="nowrap"
          v-slot="props"
        >
          <b-tag
            :type="props.row.put_on_shelves ? 'is-info' : 'is-danger'"
            size="is-medium"
          >
            {{ shelveStatus(props.row.put_on_shelves) }}
          </b-tag>
        </b-table-column>

        <b-table-column
          field="department_no"
          :label="$t('attributes.gift_department')"
          width="120"
          sortable
          header-class="nowrap"
          v-slot="props"
        >
          {{
            !!props.row.department_no
              ? `[${props.row.department_no}] ${props.row.department_name}`
              : '總公司'
          }}
        </b-table-column>

        <b-table-column
          field="employee_code"
          :label="$t('attributes.gift_employee')"
          width="120"
          sortable
          header-class="nowrap"
          v-slot="props"
        >
          {{
            !!props.row.department_no
              ? `[${props.row.employee_code}] ${props.row.employee_name}`
              : '總務'
          }}
        </b-table-column>

        <b-table-column
          field="total_quantity"
          :label="$t('attributes.gift_quantity') + '(待審)'"
          width="80"
          numeric
          sortable
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.quantity + props.row.stock }}
          <br />
          <span v-if="props.row.padding_amount > 0" style="color: Red;">
            ({{ props.row.padding_amount }})
          </span>
        </b-table-column>

        <b-table-column
          field="quantity"
          :label="$t('attributes.gift_stock_company')"
          width="80"
          numeric
          sortable
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.quantity }}
        </b-table-column>

        <b-table-column
          field="stock"
          :label="$t('attributes.gift_stock_vendor')"
          width="100"
          sortable
          numeric
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.stock }}
        </b-table-column>

        <b-table-column
          field="consumable"
          :label="$t('attributes.gift_consumable')"
          :visible="filterType === 'all'"
          width="60"
          sortable
          centered
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.consumable ? '是' : '否' }}
        </b-table-column>

        <b-table-column
          field="limit_times"
          :label="$t('attributes.gift_limit_times')"
          width="80"
          sortable
          centered
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.limit_times === 0 ? '可以' : '不可' }}
        </b-table-column>

        <b-table-column
          field="cost"
          :label="$t('attributes.gift_cost')"
          width="80"
          sortable
          numeric
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.cost }}
        </b-table-column>

        <b-table-column
          field="net_price"
          :label="$t('attributes.gift_net_price')"
          width="80"
          sortable
          numeric
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.consumable ? '-' : props.row.net_price }}
        </b-table-column>

        <b-table-column
          field="sale_price"
          :label="$t('attributes.gift_sale_price')"
          width="80"
          sortable
          numeric
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.consumable ? '-' : props.row.sale_price }}
        </b-table-column>

        <b-table-column
          field="dividend_point"
          :label="$t('attributes.gift_dividend_point')"
          width="80"
          sortable
          numeric
          header-class="nowrap"
          v-slot="props"
        >
          {{ props.row.consumable ? '-' : props.row.dividend_point }}
        </b-table-column>

        <b-table-column
          v-if="!readonly"
          :width="70"
          label="功能"
          centered
          v-slot="props"
        >
          <div class="buttons is-centered">
            <b-button
              type="is-primary"
              size="is-small"
              :disabled="!hasPermission(PERMISSION_TYPE.GiftsProcess)"
              @click.prevent="onModify(props.row)"
            >
              {{ $t('attributes.modify') }}
            </b-button>
          </div>
        </b-table-column>

        <template #empty>
          <section class="section">
            <div
              v-if="!loadingItems"
              class="content has-text-grey has-text-centered"
            >
              <p>
                <b-icon icon="emoticon-sad" size="is-large" />
              </p>
            </div>
          </section>
        </template>
      </b-table>
      <div
        id="showMore"
        style="display: block; width:120px; margin: 0 auto; padding: 0.5rem; font-size: 18px; text-align: center; cursor: pointer;"
      >
        <b-button type="is-link" :label="showText" @click.prevent="showMore">
        </b-button>
      </div>
    </section>
    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-form :gift="editingItem" :sending="sending" @send="onSend" />
    </b-modal>

    <b-modal
      :active.sync="isPostFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-post-form
        :gifts="selectedGifts"
        :sending="sending"
        @send="sendPosting"
      />
    </b-modal>

    <b-modal
      :active.sync="isExportMonthFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <export-month-form :sending="sending" @send="onExportMonth" />
    </b-modal>

    <b-modal
      :active.sync="isExportInvoicingFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <export-invoicing-form :sending="sending" @send="onExportInvoicing" />
    </b-modal>

    <b-modal
      :active.sync="isGiftPurchaseFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-purchase-form
        :gift-purchase="createdGift"
        :sending="sending"
        @send="onCreateGiftPurchase"
      />
    </b-modal>

    <b-modal
      :active.sync="isGiftPurchaseImportFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-purchase-import-form
        :gift-purchase="giftImport"
        :sending="sending"
        @send="importItem"
      />
    </b-modal>

    <b-modal
      :active.sync="isGiftTransferImportFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-transfer-import-form
        :gift-transfer="giftTransfer"
        :sending="sending"
        @send="onTransferImport"
      />
    </b-modal>

    <b-modal has-modal-card :active.sync="isImageModalActive">
      <p v-if="showGift" class="image">
        <img
          style="max-width: 450px; max-height: 800px; text-align: center;"
          :src="GiftImage(showGift.picture)"
          :alt="showGift.name"
        />
      </p>
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'
import { PERMISSION_TYPE } from '@/utils/constants'
import {
  GIFT_POSTS_API,
  GIFT_PURCHASES_API,
  GIFT_TRANSFERS_API,
  GIFTS_API
} from '@/utils/apis'
import notifyService from '@/services/notifyService'
import GiftForm from './GiftForm'
import GiftPostForm from './GiftPostForm'
import ExportMonthForm from './ExportMonthForm'
import ExportInvoicingForm from './ExportInvoicingForm'
import GiftPurchaseForm from '@/components/gifts/GiftPurchaseForm.vue'
import GiftPurchaseImportForm from '@/components/gifts/GiftPurchaseImportForm.vue'
import GiftTransferImportForm from '@/components/gifts/GiftTransferImportForm.vue'

export default {
  name: 'GiftList',
  components: {
    GiftTransferImportForm,
    GiftPurchaseImportForm,
    GiftPurchaseForm,
    GiftForm,
    GiftPostForm,
    ExportMonthForm,
    ExportInvoicingForm
  },

  props: {
    query: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const filterType = this.query.type || 'all'

    return {
      loadingItems: false,
      items: [],
      selectedItems: [],
      selectedGifts: [],
      showText: '查看更多',
      giftListTableHeight: '220px',

      consumable: this.consumableType(filterType),
      filterType: filterType,
      status: this.query.status || true,
      search: this.query.search || '',
      sortField: this.query.sort || 'sort',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',

      isFormModalActive: false,
      isPostFormModalActive: false,

      isImageModalActive: false,
      showGift: null,

      editingItem: null,
      sending: false,

      isExportMonthFormModalActive: false,
      isExportInvoicingFormModalActive: false,

      isGiftPurchaseFormModalActive: false,
      createdGift: null,

      isGiftPurchaseImportFormModalActive: false,
      giftImport: null,

      isGiftTransferImportFormModalActive: false,
      giftTransfer: null
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    ...mapGetters('app', ['hasPermission']),
    exportOptions() {
      const options = []

      if (this.hasPermission(PERMISSION_TYPE.GiftsPostsProcess)) {
        options.push({
          value: 'month',
          name: '月報表'
        })
      }

      if (this.hasPermission(PERMISSION_TYPE.GiftsTransfers)) {
        options.push({
          value: 'stock',
          name: ' 庫存報表'
        })
      }

      if (this.hasPermission(PERMISSION_TYPE.GiftsReviewsProcess)) {
        options.push({
          value: 'review',
          name: ' 進銷明細'
        })
      }

      return options
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    showMore() {
      if (this.giftListTableHeight === '220px') {
        this.giftListTableHeight = '580px'
        this.showText = '查看較少'
      } else {
        this.giftListTableHeight = '220px'
        this.showText = '查看更多'
      }
    },
    async refresh() {
      await this.loadItems()
    },
    async loadItems() {
      try {
        this.status = this.status === undefined ? '' : this.status
        const api = `${GIFTS_API}`
        const params = {
          consumable: this.consumable,
          status: this.status,
          department: null,
          employee: null,
          search: this.search,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc'
        }
        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        this.items = res.data.data

        if (this.selectedGifts.length > 0) {
          const list = this.selectedGifts.map(t => t.id)
          this.selectedGifts = this.items.filter(x => list.includes(x.id))
          this.selectedItems = [...this.selectedGifts]
          this.$emit('selected', this.selectedGifts)
        }
      } catch (e) {
        notifyService.fetch_error('gift', e)
      } finally {
        this.loadingItems = false
      }
    },

    getQuery(query) {
      this.filterType = query.type || 'all'
      this.consumable = this.consumableType(this.filterType)
      this.status = query.status
      this.department = query.dep
      this.employee = query.emp
      this.search = query.search || ''
      this.sortField = query.sort || 'sort'
      this.sortOrder = query.order || 'asc'
    },
    updateQuery() {
      const query = {
        type: this.filterType,
        status: this.status,
        dep: this.department,
        emp: this.employee,
        search: this.search,
        sort: this.sortField,
        order: this.sortOrder
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterType: debounce(function() {
      let consumable = null
      if (this.filterType === 'true') consumable = true
      else if (this.filterType === 'false') consumable = false
      this.consumable = consumable

      this.updateQuery()
    }, 500),
    onFilter: debounce(function() {
      this.updateQuery()
    }, 500),
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },

    onSelected(items) {
      const list = items.map(t => t.id)
      this.selectedGifts = this.items.filter(x => list.includes(x.id))

      this.$emit('selected', this.selectedGifts)
    },

    onAdd() {
      this.editingItem = {
        id: null,
        picture: '',
        quantity: 0,
        stock: 0,
        department_no: null,
        department_name: null,
        employee_code: null,
        consumable: false,
        limit_times: 1,
        cost: 0,
        net_price: 0,
        sale_price: 0,
        dividend_point: 0,
        put_on_shelves: true
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.editingItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.editingItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        const res = await this.axios.post(GIFTS_API, item)
        this.isFormModalActive = false
        await this.loadItems()
        this.$buefy.dialog.confirm({
          message: '是否繼續做進貨處理?',
          onConfirm: () => {
            const gift = res.data.data
            this.createdGift = {
              id: null,
              gift_id: gift.id,
              gift_name: gift.name,
              date: null,
              quantity: gift.quantity,
              net_price: gift.net_price,
              cost: gift.cost
            }
            this.isGiftPurchaseFormModalActive = true
          }
        })
        notifyService.success(this.$t('attributes.add_succeed'))
        this.$emit('updated')
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${GIFTS_API}/${item.id}`

        this.sending = true
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems()
        this.$emit('updated')
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async onCreateGiftPurchase(item) {
      try {
        this.sending = true
        const res = await this.axios.post(GIFT_PURCHASES_API, item)
        notifyService.success(this.$t('attributes.add_succeed'))
        await this.orderItem(res.data.data)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
        this.isGiftPurchaseFormModalActive = false
      }
    },
    async orderItem(item) {
      try {
        const api = `${GIFT_PURCHASES_API}/${item.id}/order`

        this.sending = true
        const res = await this.axios.put(api, {})
        this.giftImport = res.data.data
        this.isGiftPurchaseFormModalActive = false
        this.isGiftPurchaseImportFormModalActive = true
        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async importItem(item) {
      try {
        const api = `${GIFT_PURCHASES_API}/${this.giftImport.id}/import`

        this.sending = true
        const res = await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))
        await this.loadItems()
        this.$buefy.dialog.confirm({
          message: '是否把進貨庫存轉出到公司?',
          onConfirm: () => {
            const gift = res.data.data
            this.giftTransfer = {
              gift_id: gift.gift_id,
              gift_name: gift.gift_name,
              date: null,
              quantity: 1,
              origin_stock: gift.gift.stock,
              origin_quantity: gift.quantity,
              unit_cost: null
            }

            this.isGiftTransferImportFormModalActive = true
          }
        })
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
        this.isGiftPurchaseImportFormModalActive = false
      }
    },
    async onTransferImport(item) {
      try {
        const api = `${GIFT_TRANSFERS_API}/import`
        this.sending = true
        await this.axios.post(api, item)
        notifyService.success(this.$t('attributes.add_succeed'))

        this.$emit('updated')
        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
        this.isGiftTransferImportFormModalActive = false
      }
    },
    onPosting() {
      this.isPostFormModalActive = true
    },
    async sendPosting(item) {
      try {
        this.sending = true
        await this.axios.post(GIFT_POSTS_API, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems(this.gifts)
        this.$emit('updated')
      } catch (e) {
        notifyService.error('過帳失敗', e)
      } finally {
        this.sending = false
        this.isPostFormModalActive = false
      }
    },
    canExport() {
      return !this.readonly && this.exportOptions.length > 0
    },
    onExport(option) {
      switch (option) {
        case 'month':
          this.isExportMonthFormModalActive = true
          break
        case 'stock':
          this.onExportStock()
          break
        case 'review':
          this.isExportInvoicingFormModalActive = true
          break
      }
    },
    async onExportMonth(month) {
      try {
        const api = `${GIFT_POSTS_API}/export`
        const data = {
          gifts: this.selectedGifts.map(g => g.id),
          date: month
        }

        const config = {
          headers: { Accept: 'application/vnd.ms-excel' },
          responseType: 'blob'
        }

        this.sending = true
        const response = await this.axios.post(api, data, config)

        const yearMonth = this.$dayjs(month).format('YYYYMM')
        const date = this.$dayjs().format('YYYYMMDD')
        const fileName = `贈品存貨月報表_${yearMonth}_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('匯出失敗', e)
      } finally {
        this.sending = false
        this.isExportMonthFormModalActive = false
      }
    },
    async onExportStock() {
      try {
        const api = `${GIFTS_API}/stock-export`
        const data = {
          gifts: this.selectedGifts.map(g => g.id)
        }

        const config = {
          headers: { Accept: 'application/vnd.ms-excel' },
          responseType: 'blob'
        }

        this.sending = true
        const response = await this.axios.post(api, data, config)

        const date = this.$dayjs().format('YYYYMMDD')
        const fileName = `贈品庫存報表_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('匯出失敗', e)
      } finally {
        this.sending = false
      }
    },
    async onExportInvoicing(start_date, end_date) {
      try {
        const api = `${GIFTS_API}/invoicing-export`
        end_date.setHours(23, 59, 59, 999)
        const data = {
          gifts: this.selectedGifts.map(g => g.id),
          start: start_date,
          end: end_date
        }

        const config = {
          headers: { Accept: 'application/vnd.ms-excel' },
          responseType: 'blob'
        }

        this.sending = true
        const response = await this.axios.post(api, data, config)

        const date = this.$dayjs().format('YYYYMMDD')
        const fileName = `贈品進銷報表_${date}`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('匯出失敗', e)
      } finally {
        this.sending = false
        this.isExportInvoicingFormModalActive = false
      }
    },
    async onCheckLowQuantity() {
      try {
        const api = `${GIFTS_API}/check-low-quantity`
        this.loadingItems = true
        await this.axios.get(api)
        this.$buefy.dialog.alert('檢查完畢!!')
      } catch (e) {
        notifyService.fetch_error('gift', e)
      } finally {
        this.loadingItems = false
      }
    },
    consumableType(filterType) {
      if (filterType === 'true') return true
      if (filterType === 'false') return false
      return null
    },
    onClickImage(gift) {
      this.showGift = gift
      this.isImageModalActive = true
    },
    rowClass(row) {
      if (row.quantity + row.stock === 0) {
        return 'quantity-zero'
      } else {
        return ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
<style>
.quantity-zero {
  color: #bebebe;
}
</style>
